import { useState, useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { SqButton } from "@/components/squircle-ui/button";
import { RiFolderChartLine } from "@remixicon/react";
import { MessageCreative, MessageType } from "../../types";

type Props = {
  msg: MessageType;
  sections: { id: string; title: string }[];
  showIcon?: boolean;
  metrics: { id: string; label: string }[];
  creatives?: MessageCreative[];
};

const SelectedSourcesPopover = ({ msg, metrics, sections, creatives }: Props) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const triggerPositionRef = useRef<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) {
      triggerPositionRef.current = null;
      return;
    }

    // Store initial trigger position when opening
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      triggerPositionRef.current = {
        top: rect.top,
        left: rect.left,
      };
    }

    const handleScroll = () => {
      if (!triggerRef.current || !triggerPositionRef.current) return;

      const currentRect = triggerRef.current.getBoundingClientRect();
      const initialPos = triggerPositionRef.current;

      // Close only if trigger has moved significantly (more than 2px to account for minor layout shifts)
      if (
        Math.abs(currentRect.top - initialPos.top) > 2 ||
        Math.abs(currentRect.left - initialPos.left) > 2
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const maxVisibleThumbnails = 3;
  const visibleCreatives = creatives?.slice(0, maxVisibleThumbnails) || [];
  const remainingCount = (creatives?.length || 0) - maxVisibleThumbnails;
  const hasCreatives = creatives && creatives.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div ref={triggerRef}>
          {hasCreatives ? (
            <SqButton variant="outline" size="md" className="px-2 py-1.5 gap-[4px]">
              {visibleCreatives.map((creative) => (
                <div
                  key={creative.id}
                  className="w-[20px] h-[20px] rounded-[4px] overflow-hidden"
                >
                  {creative.thumbnail_url ? (
                    <img
                      src={creative.thumbnail_url}
                      alt={creative.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500">
                      {creative.type === "video" ? "🎬" : "🖼️"}
                    </div>
                  )}
                </div>
              ))}
              {remainingCount > 0 && (
                <div className="flex items-center justify-center text-[11px] font-medium text-brand-primary-800">
                  +{remainingCount}
                </div>
              )}
            </SqButton>
          ) : (
            <SqButton
              variant="outline"
              size={"md"}
              className="gap-[6px] px-2 py-1 max-w-[150px]"
            >
              <RiFolderChartLine size={14} className="shrink-0 text-brand-primary-400" />
              <span className="mt-[2px] truncate text-[11px] text-brand-primary-800">
                {msg.report?.title}
              </span>
            </SqButton>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-[240px] p-0 shadow-none mb-1 rounded-[8px] border-border-gray flex justify-center items-start flex-col drop-shadow-sm squircle bg-white border-0 squircle-outline-[1px] squircle-outline-color-border-gray"
      >
        <div className="max-h-[320px] w-full flex flex-col items-start flex-1 overflow-y-auto">
          {/* Creatives section */}
          {hasCreatives ? (
            <>
              <div className="w-full flex min-h-[32px] px-[8px] pl-[9px] py-[6px] items-center text-[10px] font-medium text-brand-primary-600 tracking-[1px] uppercase leading-[12px]">
                <span className="text-brand-primary-600 text-[10px] font-medium leading-[12px] tracking-[1px] uppercase">
                  Creatives
                </span>
              </div>
              <div className="w-full">
                {creatives.map((creative) => (
                  <div
                    key={creative.id}
                    className="flex min-h-[36px] px-[8px] pl-[9px] pb-[6px] items-center gap-2"
                  >
                    <div className="w-[20px] h-[20px] rounded-[4px] overflow-hidden shrink-0 bg-gray-100">
                      {creative.thumbnail_url ? (
                        <img
                          src={creative.thumbnail_url}
                          alt={creative.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-500">
                          {creative.type === "video" ? "🎬" : "🖼️"}
                        </div>
                      )}
                    </div>
                    <span className="text-[12px] font-normal text-brand-primary-new/80 truncate">
                      {creative.name}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {/* Separator between creatives and sections */}
          {hasCreatives && sections?.length ? (
            <div className="w-full p-1">
              <Separator />
            </div>
          ) : null}

          {sections?.length ? (
            <>
              <div className="w-full flex min-h-[32px] px-[8px] pl-[9px] py-[6px] items-center text-[10px] font-medium text-brand-primary-600 tracking-[1px] uppercase leading-[12px]">
                <span className="text-brand-primary-600 text-[10px] font-medium leading-[12px] tracking-[1px] uppercase">
                  Sections
                </span>
              </div>
              <div className="w-full">
                {sections.map((section) => (
                  <div
                    key={section.id}
                    className="flex min-h-[32px] px-[8px] pl-[9px] pb-[6px] items-center"
                  >
                    <span className="text-[12px] font-normal text-brand-primary-new/80">
                      {section.title}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {/* Separator between sections/creatives and metrics */}
          {(hasCreatives || sections?.length) && metrics?.length ? (
            <div className="w-full p-1">
              <Separator />
            </div>
          ) : null}

          {metrics?.length ? (
            <>
              <div className="w-full flex min-h-[32px] px-[8px] pl-[9px] py-[6px] items-center text-[10px] font-medium text-brand-primary-600 tracking-[1px] uppercase leading-[12px]">
                <span className="text-brand-primary-600 text-[10px] font-medium leading-[12px] tracking-[1px] uppercase">
                  Metrics
                </span>
              </div>
              <div className="w-full">
                {metrics.map((metric) => (
                  <div
                    key={metric.id}
                    className="flex min-h-[32px] px-[8px] pl-[9px] pb-[6px] items-center gap-1"
                  >
                    <span className="text-[12px] font-normal text-brand-primary-new/80">
                      {metric.label}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SelectedSourcesPopover;
